import { Injectable } from '@angular/core';
import {
  FormResponse,
  AutomationTrigger,
  AutomationLog,
  FormAnswer,
  TriggerCondition,
  AutomationTriggerConfig
} from '../models/form.model';

/**
 * Service for executing automation triggers on form submissions
 * Supports webhook, task-workflow, create-task, email-notification, and record-update trigger types
 */
@Injectable({
  providedIn: 'root'
})
export class FormAutomationService {
  constructor() {}

  /**
   * Execute all applicable automation triggers for a form response
   * @param response - The form response that triggered automations
   * @param triggers - Array of automation triggers to evaluate and execute
   * @returns Promise resolving to array of execution logs
   * @example
   * const logs = await formAutomationService.executeTriggers(response, triggers);
   * response.automationLog = logs;
   */
  async executeTriggers(
    response: FormResponse,
    triggers: AutomationTrigger[]
  ): Promise<AutomationLog[]> {
    //console.log('[FormAutomationService] executeTriggers:', { 
    //   responseId: response.id, 
    //   triggerCount: triggers.length,
    //   timestamp: new Date().toISOString() 
    // });

    const logs: AutomationLog[] = [];

    for (const trigger of triggers) {
      const startTime = Date.now();
      
      // Skip disabled triggers
      if (!trigger.enabled) {
        console.log('[FormAutomationService] Skipping disabled trigger:', trigger.id);
        logs.push(this.createLog(trigger, 'skipped', startTime));
        continue;
      }

      // Evaluate conditions
      if (trigger.conditions && trigger.conditions.length > 0) {
        const conditionsMet = this.evaluateConditions(trigger.conditions, response.answers);
        if (!conditionsMet) {
          console.log('[FormAutomationService] Conditions not met for trigger:', trigger.id);
          logs.push(this.createLog(trigger, 'skipped', startTime, undefined, 0, 'Conditions not met'));
          continue;
        }
      }

      // Execute trigger based on type
      try {
        await this.executeTrigger(trigger, response);
        const duration = Date.now() - startTime;
        logs.push(this.createLog(trigger, 'success', startTime, undefined, duration));
        console.log('[FormAutomationService] Trigger executed successfully:', trigger.id, duration + 'ms');
      } catch (error) {
        const duration = Date.now() - startTime;
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        logs.push(this.createLog(trigger, 'failed', startTime, errorMessage, duration));
        console.error('[FormAutomationService] Trigger execution failed:', trigger.id, errorMessage);
      }
    }

    return logs;
  }

  /**
   * Evaluate trigger conditions against form answers
   * @param conditions - Array of conditions to evaluate (AND logic)
   * @param answers - Form answers to evaluate against
   * @returns true if all conditions pass, false otherwise
   */
  evaluateConditions(conditions: TriggerCondition[], answers: FormAnswer[]): boolean {
    if (!conditions || conditions.length === 0) {
      return true;
    }

    return conditions.every(condition => {
      const answer = answers.find(a => a.fieldId === condition.fieldId);
      const value = answer?.value;

      switch (condition.operator) {
        case 'equals':
          return String(value) === condition.value;
        
        case 'contains':
          return value && String(value).includes(condition.value || '');
        
        case 'regex':
          if (!condition.value) return false;
          try {
            const regex = new RegExp(condition.value);
            return regex.test(String(value));
          } catch (e) {
            console.error('[FormAutomationService] Invalid regex:', condition.value, e);
            return false;
          }
        
        case 'isEmpty':
          return !value || String(value).trim() === '';
        
        case 'isNotEmpty':
          return !!value && String(value).trim() !== '';
        
        default:
          console.warn('[FormAutomationService] Unknown operator:', condition.operator);
          return false;
      }
    });
  }

  /**
   * Resolve trigger parameters by mapping form field values
   * @param config - Trigger configuration containing field mappings
   * @param answers - Form answers
   * @returns Map of resolved parameter values
   */
  resolveTriggerParameters(
    config: AutomationTriggerConfig,
    answers: FormAnswer[]
  ): Map<string, any> {
    const resolved = new Map<string, any>();

    if (config.mappings) {
      for (const [targetField, sourceFieldId] of Object.entries(config.mappings)) {
        const answer = answers.find(a => a.fieldId === sourceFieldId);
        resolved.set(targetField, answer?.value);
      }
    }

    return resolved;
  }

  /**
   * Execute a single trigger based on its type
   * @private
   */
  private async executeTrigger(trigger: AutomationTrigger, response: FormResponse): Promise<void> {
    switch (trigger.type) {
      case 'webhook':
        await this.executeWebhookTrigger(trigger.config, response);
        break;
      
      case 'task-workflow':
        await this.executeTaskWorkflowTrigger(trigger.config, response);
        break;
      
      case 'create-task':
        await this.executeCreateTaskTrigger(trigger.config, response);
        break;
      
      case 'email-notification':
        await this.executeEmailNotificationTrigger(trigger.config, response);
        break;
      
      case 'record-update':
        await this.executeRecordUpdateTrigger(trigger.config, response);
        break;
      
      default:
        throw new Error(`Unsupported trigger type: ${trigger.type}`);
    }
  }

  /**
   * Execute webhook trigger (stub implementation)
   * @private
   */
  private async executeWebhookTrigger(config: AutomationTriggerConfig, response: FormResponse): Promise<void> {
    // Stub: In real implementation, would make HTTP POST to config.url
    console.log('[FormAutomationService] executeWebhookTrigger:', config.url);
    // Simulate async operation
    await new Promise(resolve => setTimeout(resolve, 10));
  }

  /**
   * Execute task workflow trigger (stub implementation)
   * @private
   */
  private async executeTaskWorkflowTrigger(config: AutomationTriggerConfig, response: FormResponse): Promise<void> {
    // Stub: In real implementation, would call ProjectTaskService to advance workflow
    console.log('[FormAutomationService] executeTaskWorkflowTrigger:', config);
    await new Promise(resolve => setTimeout(resolve, 10));
  }

  /**
   * Execute create task trigger (stub implementation)
   * @private
   */
  private async executeCreateTaskTrigger(config: AutomationTriggerConfig, response: FormResponse): Promise<void> {
    // Stub: In real implementation, would call task creation endpoint
    console.log('[FormAutomationService] executeCreateTaskTrigger:', config);
    await new Promise(resolve => setTimeout(resolve, 10));
  }

  /**
   * Execute email notification trigger (stub implementation)
   * @private
   */
  private async executeEmailNotificationTrigger(config: AutomationTriggerConfig, response: FormResponse): Promise<void> {
    // Stub: In real implementation, would call email/notification service
    console.log('[FormAutomationService] executeEmailNotificationTrigger:', config);
    await new Promise(resolve => setTimeout(resolve, 10));
  }

  /**
   * Execute record update trigger (stub implementation)
   * @private
   */
  private async executeRecordUpdateTrigger(config: AutomationTriggerConfig, response: FormResponse): Promise<void> {
    // Stub: In real implementation, would call entity update endpoints
    console.log('[FormAutomationService] executeRecordUpdateTrigger:', config);
    await new Promise(resolve => setTimeout(resolve, 10));
  }

  /**
   * Create automation log entry
   * @private
   */
  private createLog(
    trigger: AutomationTrigger,
    status: 'success' | 'failed' | 'timeout' | 'skipped',
    startTime: number,
    error?: string,
    durationMs?: number,
    message?: string
  ): AutomationLog {
    return {
      triggerId: trigger.id,
      triggerName: trigger.name,
      type: trigger.type,
      status,
      executedAt: new Date(startTime).toISOString(),
      durationMs: durationMs || Date.now() - startTime,
      error: error || message
    };
  }
}
